import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Chip, Stack, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { usePageTitle } from '../../components/shell/AppShell';
import { RouteDataGrid } from '../../components/data-grid/RouteDataGrid';
import type { RowAction } from '../../components/data-grid/RouteDataGrid';
import { fmtDateTime } from '../../utils/format';
import type { ExportColumn } from '../../utils/export';
import type { StickerHeaderTemplate } from '../../api/types';
import {
  useDeleteStickerHeaderTemplate, useOperatorsList, useStickerHeaderTemplatesList,
} from '../../api/queries';
import { useConfirm } from '../../components/dialogs/DialogProvider';
import { HeaderTemplateDialog } from './HeaderTemplateDialog';

/**
 * ACES-only — manages reusable per-batch header designs (text + logo).
 * The page lists every saved template; create / edit / delete via the
 * dialog. Templates are applied to batches from the batch detail page.
 *
 * The dialog is a drop-in designer: line list + logo source picker.
 * Live preview is intentionally minimal here — the print-preview page
 * already produces the actual rendered output.
 */
export default function StickerHeaderTemplatesPage() {
  usePageTitle('QR Code header templates');
  const list = useStickerHeaderTemplatesList();
  const operators = useOperatorsList();
  const operatorsQ = useQuery({ queryKey: operators.queryKey, queryFn: operators.listFn, staleTime: 60_000 });
  const del = useDeleteStickerHeaderTemplate();
  const confirm = useConfirm();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<StickerHeaderTemplate | null>(null);

  const operatorName = (id: string | null) =>
    !id ? '—' : (operatorsQ.data?.find((o) => o.id === id)?.displayName ?? id.slice(0, 8) + '…');

  const columns = useMemo<GridColDef<StickerHeaderTemplate>[]>(() => [
    { field: 'name', headerName: 'Name', flex: 1.2,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2">{p.row.name}</Typography>
        </Box>
      ) },
    { field: 'logoSource', headerName: 'Logo source', width: 130,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip size="small" label={p.row.logoSource} />
        </Box>
      ) },
    { field: 'logoOperatorId', headerName: 'Operator', flex: 1,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" color={p.row.logoOperatorId ? 'text.primary' : 'text.secondary'}>
            {operatorName(p.row.logoOperatorId)}
          </Typography>
        </Box>
      ) },
    { field: 'hasLogo', headerName: 'Logo', width: 80,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="caption" color={p.row.hasLogo ? 'success.main' : 'text.secondary'}>
            {p.row.hasLogo ? '✓' : '—'}
          </Typography>
        </Box>
      ) },
    { field: 'updatedAt', headerName: 'Updated', width: 180,
      renderCell: (p) => <Typography variant="caption">{fmtDateTime(p.row.updatedAt)}</Typography> },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [operatorsQ.data]);

  const exportColumns = useMemo<ExportColumn<StickerHeaderTemplate>[]>(() => [
    { key: 'name',           label: 'Name' },
    { key: 'logoSource',     label: 'Logo source' },
    { key: 'logoOperatorId', label: 'Operator',  format: (v) => operatorName(v as string | null) },
    { key: 'hasLogo',        label: 'Has logo',  format: (v) => v ? 'yes' : 'no' },
    { key: 'createdAt',      label: 'Created',   format: (v) => fmtDateTime(v) },
    { key: 'updatedAt',      label: 'Updated',   format: (v) => fmtDateTime(v) },
    { key: 'notes',          label: 'Notes' },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [operatorsQ.data]);

  const actions = useMemo<RowAction<StickerHeaderTemplate>[]>(() => [
    { label: 'Edit',
      onClick: (row) => { setEditing(row); setDialogOpen(true); } },
    { label: 'Delete',
      destructive: true,
      onClick: async (row) => {
        const ok = await confirm({
          title: 'Delete header template',
          message: <>Delete header template <strong>{row.name}</strong>? Batches that already snapshot it stay unchanged.</>,
          confirmLabel: 'Delete',
          destructive: true,
        });
        if (!ok) return;
        await del.mutateAsync(row.id);
      } },
  ], [del, confirm]);

  return (
    <>
      <Stack spacing={2}>
        <Typography variant="h4">QR Code header templates</Typography>
        <Typography variant="body2" color="text.secondary">
          ACES-only. Each header template is a reusable design (text
          lines + optional logo) that prints above the QR code on
          every QR Code in a batch. Applying a template to a batch
          snapshots the design — later edits to the template don't
          mutate already-printed batches.
        </Typography>

        <RouteDataGrid<StickerHeaderTemplate>
          entityName="QR Code header templates"
          queryKey={list.queryKey}
          listFn={list.listFn}
          columns={columns}
          searchFields={[{ kind: 'text', key: 'name', label: 'Name' }]}
          exportColumns={exportColumns}
          reportTitle="ARMS — QR Code header templates"
          actions={actions}
          primaryAction={{ label: '+ New template',
            onClick: () => { setEditing(null); setDialogOpen(true); } }}
          onRowClick={(row) => { setEditing(row); setDialogOpen(true); }}
          emptyMessage="No header templates yet — create one to apply it to a batch."
        />

        <HeaderTemplateDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          template={editing}
          operators={operatorsQ.data ?? []}
        />
      </Stack>
    </>
  );
}
